<template>
  <component-wrapper
    :class="{
      'product-teasers-fi': true,
      'product-teasers-fi--light': light,
      'product-teasers-fi--content': showContent,
    }"
  >
    <grid-container-fi grid-class="fi-gap-zero md:fi-gap-zero lg:fi-gap-large">
      <div class="product-teasers-fi__products">
        <div class="product-teasers-fi__products-wrapper">
          <div
            v-if="isSlider || currentFields.headline?.value || isEditMode"
            class="product-teasers-fi__products__head"
          >
            <div
              v-if="!isEditMode"
              class="product-teasers-fi__products__headline"
              data-test="product-teasers-headline"
            >
              {{ currentFields.headline.value }}
            </div>
            <!-- eslint-disable vue/no-v-html -->
            <div
              v-if="isEditMode"
              class="product-teasers-fi__products__headline"
              v-html="currentFields.headline.editable"
            />
            <!-- eslint-enable vue/no-v-html -->

            <div class="product-teasers-fi__slider-controls">
              <carousel-slider-controls-fi
                v-if="isSlider"
                :carousel="carouselRef"
              />
            </div>
          </div>

          <carousel-fi
            v-if="catalogData?.length"
            :key="changesCount"
            :ref="(carousel) => (carouselRef = carousel)"
            v-intersect.once="onIntersect"
            :class="{
              'product-teasers-fi__slider': true,
              'product-teaser-fi__slider--no-slider': !isSlider,
              'product-teaser-fi__slider--with-content': showContent,
            }"
            :settings="sliderSettings"
            theme="product-teasers"
            no-arrows
            clipped-right
          >
            <carousel-slide-fi
              v-for="(item, index) in catalogData"
              :key="item.id"
              theme="product-teasers"
            >
              <div class="product-teasers-fi__slide">
                <product-tile-fi
                  v-if="isVariant"
                  :product="item"
                  :best-price="bestPrices[item.id]"
                  :best-price-loading="bestPriceLoading"
                  :out-of-stock="retailerOutOfStock[index]"
                  :is-recommendation="isRecommendation"
                  :is-guided-recommandation="isGuidedRecommandation"
                  @product-added="$emit('product-added', $event)"
                  @tile-clicked="trackSelectItem(item, !isStandaloneVariant)"
                />
                <product-category-tile-fi
                  v-else
                  :category="item"
                  :is-guided-recommandation="isGuidedRecommandation"
                  @tile-clicked="trackSelectItem(item)"
                />
              </div>
            </carousel-slide-fi>
          </carousel-fi>
        </div>
      </div>
      <div
        v-if="showContent"
        class="product-teasers-fi__content"
      >
        <div class="product-teasers-fi__content-container">
          <richtext-fi
            v-if="currentFields.content"
            class="product-teasers-fi__text"
            is-theme-light
            :html-content="currentFields.content"
          />
        </div>
      </div>
    </grid-container-fi>
  </component-wrapper>
</template>

<script>
import { CarouselFi, CarouselSlideFi, RichtextFi } from 'atoms';
import CarouselSliderControlsFi from 'atoms/carousel-fi/CarouselSliderControlsFi.vue';
import ComponentWrapper from 'components/component-wrapper/ComponentWrapper';
import GridContainerFi from 'components/grid-fi/GridContainerFi';
import ProductCategoryTileFi from 'components/product-category/product-category-tile/ProductCategoryTileFi';
import ProductTileFi from 'components/product-tile-fi/ProductTileFi';
import GETPRODUCTCATEGORYLISTBYID from 'gql/getProductCategoryListById.gql';
import GETPRODUCTLISTBYID from 'gql/getProductListById.gql';
import GET_RETAILER_AVAILABILITY_FOR_IDS from 'gql/getRetailerAvailabilityForIds.gql';
import breakpointMixin from 'mixins/breakpointMixin';
import trackingMixin from 'mixins/trackingMixin';
import screenWidths from 'tailwind/screenWidths';

const importGET_MULTIPLE_BEST_PRICES = () => import('gql/getMultipleBestPrices.gql');

export default {
  name: 'ProductTeasersFi',
  components: {
    CarouselFi,
    CarouselSlideFi,
    CarouselSliderControlsFi,
    ComponentWrapper,
    GridContainerFi,
    RichtextFi,
    ProductTileFi,
    ProductCategoryTileFi,
  },
  mixins: [breakpointMixin, trackingMixin],
  inject: ['fields'],
  props: {
    customFields: {
      type: Object,
      default: () => ({}),
    },
    light: {
      type: Boolean,
      default: false,
    },
    isRecommendation: {
      type: Boolean,
      default: false,
    },
    isGuidedRecommandation: {
      type: Boolean,
      default: false,
    },
    showVariantsCount: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['product-added'],
  data() {
    return {
      getProductListById: null,
      bestPrices: {},
      bestPriceLoading: true,
      changesCount: 0,
      catalogData: null,
      GET_MULTIPLE_BEST_PRICES: null,
      isMounted: false,
      carouselRef: null,
    };
  },
  computed: {
    isVariant() {
      return this.currentFields.productList;
    },
    isStandaloneVariant() {
      return Object.keys(this.customFields).length === 0;
    },
    currentFields() {
      return this.isStandaloneVariant ? this.fields : this.customFields;
    },
    productIds() {
      return this.currentFields.productList?.value?.split('|') || [];
    },
    familyIds() {
      return (
        (this.currentFields.familyList?.value && this.currentFields.familyList.value.split('|')) ||
        []
      );
    },
    productQuery() {
      return this.productIds.map((id) => ({
        qty: 0,
        productCode: id,
      }));
    },
    showContent() {
      if (this.isRecommendation || this.currentFields.hideContent?.value) {
        return false;
      }

      return this.currentFields.content?.value || this.isEditMode;
    },
    sliderSettings() {
      return {
        rewind: false,
        pagination: false,
        perPage: 1,
        perMove: 1,
        destroy: false,
        gap: 24,
        noDrag: 'input, .no-drag',
        breakpoints: {
          [screenWidths.xs]: {
            perPage: 1,
            destroy: false,
          },
          [screenWidths.sm]: {
            perPage: 2,
            destroy: false,
          },
          [screenWidths.md]: {
            perPage: 3,
            destroy: false,
          },
          [screenWidths.lg]: {
            perPage: this.showContent ? 2 : 4,
            destroy: false,
          },
          [screenWidths.xl]: {
            perPage: this.showContent ? 3 : 5,
            destroy: false,
          },
        },
      };
    },
    visibleSlidesCount() {
      if (this.isXL) {
        return this.showContent ? 3 : 5;
      }
      if (this.isLG) {
        return this.showContent ? 2 : 4;
      }
      if (this.isMD) {
        return 3;
      }
      if (this.isSM) {
        return 2;
      }
      return 1;
    },
    isSlider() {
      if (!this.isMounted) {
        return false;
      }

      return this.catalogData?.length > this.visibleSlidesCount;
    },
    isRetailConnectCart() {
      return this.$fischer.shop.isRetailConnectCart();
    },
    isShop() {
      return this.$fischer.shop.isClosedShop() || this.$fischer.shop.isOnlineShop();
    },
    retailerOutOfStock() {
      return (this.getRetailerAvailabilityForIds || []).map((availability) => {
        if (!availability) {
          // something went wrong, default to 'can search for retailers'
          return false;
        }
        return availability.stock === 'empty' || availability.stock === null;
      });
    },
  },
  watch: {
    catalogData() {
      this.changesCount += 1;
    },
  },
  mounted() {
    this.isMounted = true;
    importGET_MULTIPLE_BEST_PRICES().then((imported) => {
      this.GET_MULTIPLE_BEST_PRICES = imported.default;
    });
  },
  apollo: {
    getProductCategoryListById: {
      query: GETPRODUCTCATEGORYLISTBYID,
      errorPolicy: 'all',
      variables() {
        return {
          language: this.$fischer.catalogLocale(),
          ids: this.familyIds.join(','),
          channel: this.$fischer.brandId(),
        };
      },
      skip() {
        return !this.familyIds?.length;
      },
      result({ data }) {
        if (data?.getProductCategoryListById) {
          this.catalogData = data.getProductCategoryListById;
        }
      },
      error() {
        this.error = true;
      },
    },
    getProductListById: {
      query: GETPRODUCTLISTBYID,
      errorPolicy: 'all',
      variables() {
        return {
          baseSiteId: this.$fischer.baseSiteId(),
          channel: this.$fischer.brandId(),
          language: this.$fischer.catalogLocale(),
          accessToken: this.$fischer.user.accessToken(),
          userId: this.$fischer.user.id(),
          ids: this.productIds.join(','),
        };
      },
      skip() {
        return !this.productIds?.length;
      },
      result({ data }) {
        if (data?.getProductListById) {
          this.catalogData = data.getProductListById;
        }
      },
      error() {
        this.error = true;
      },
    },
    getMultipleBestPrices: {
      query() {
        return this.GET_MULTIPLE_BEST_PRICES;
      },
      errorPolicy: 'all',
      variables() {
        return {
          baseSiteId: this.$fischer.baseSiteId(),
          language: this.$fischer.siteLanguage(),
          userId: this.$fischer.user.id(),
          accessToken: this.$fischer.user.accessToken(),
          queries: this.productQuery,
        };
      },
      result({ data }) {
        if (!data?.getMultipleBestPrices) {
          console.error('Did not receive best prices');
          return;
        }
        data.getMultipleBestPrices.forEach((bestPrice) => {
          if (bestPrice) {
            this.bestPrices[bestPrice.productCode] = bestPrice;
          }
        });
        this.bestPriceLoading = false;
      },
      skip() {
        return (
          !this.GET_MULTIPLE_BEST_PRICES ||
          !this.getProductListById ||
          !this.isShop ||
          this.isFite ||
          this.isEditMode
        );
      },
      error() {
        this.error = true;
        this.bestPriceLoading = false;
      },
    },
    getRetailerAvailabilityForIds: {
      query: GET_RETAILER_AVAILABILITY_FOR_IDS,
      variables() {
        return {
          baseSiteId: this.$fischer.baseSiteId(),
          productIds: this.productIds,
          useGtins: this.$fischer.useGtins(),
        };
      },
      skip() {
        return IS_SSR || !this.isRetailConnectCart || this.productIds.length === 0;
      },
    },
  },
  methods: {
    onIntersect() {
      this.trackViewItemList(this.catalogData);
    },
  },
};
</script>

<style scoped lang="scss">
@import './product-teasers-fi';
</style>
